import { useEffect, useState } from 'react'
import { Button } from '@w7/ui'
import { StrixMark } from '@/features/auth/StrixMark'
import { getPlayerBaseUrl } from '@/api/client'
import {
  clearCachesAndReload,
  fetchClientConfig,
  fetchDeployedVersion,
  getAppBuildId,
  needsForcedReload,
  webDeployNeedsReload,
} from './clientConfig'
import './version-gate.css'

type GateState = 'checking' | 'ready' | 'update-required' | 'reloading'

const LOCK_KEY = 'strix_backend_version_gate_lock_v1'

function readLock(): boolean {
  try {
    return sessionStorage.getItem(LOCK_KEY) === '1'
  } catch {
    return false
  }
}

function writeLock(on: boolean) {
  try {
    if (on) sessionStorage.setItem(LOCK_KEY, '1')
    else sessionStorage.removeItem(LOCK_KEY)
  } catch {
    // ignore
  }
}

function BootSplash({
  label,
  testId,
}: {
  label: string
  testId: string
}) {
  return (
    <div className="w7-version-gate w7-version-gate--boot" data-testid={testId}>
      <div className="w7-version-gate-splash" role="status" aria-live="polite" aria-busy="true">
        <StrixMark className="w7-version-gate-mark" size={56} />
        <div className="w7-version-gate-track" aria-hidden="true">
          <span className="w7-version-gate-bar" />
        </div>
        <p className="w7-version-gate-status">{label}</p>
      </div>
    </div>
  )
}

/**
 * Runs before the rest of the console. When force-reload is required, shows a
 * non-dismissible update alert — no console operations until Update now.
 */
export function VersionGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GateState>(() =>
    readLock() ? 'update-required' : 'checking',
  )
  const [busy, setBusy] = useState(false)
  const currentBuildId = getAppBuildId()
  const [requiredBuildId, setRequiredBuildId] = useState('')

  useEffect(() => {
    let cancelled = false

    async function run() {
      // Every image deploy: same-origin stamp (no Player-API push required).
      const deployed = await fetchDeployedVersion()
      if (cancelled) return
      if (
        deployed &&
        webDeployNeedsReload(getAppBuildId(), deployed.buildId)
      ) {
        writeLock(false)
        setRequiredBuildId(deployed.buildId)
        setState('reloading')
        await clearCachesAndReload()
        return
      }

      const base = getPlayerBaseUrl()
      if (!base) {
        if (!cancelled) {
          writeLock(false)
          setState('ready')
        }
        return
      }

      const config = await fetchClientConfig(base)
      if (cancelled) return

      if (needsForcedReload(config?.backendUi)) {
        writeLock(true)
        setRequiredBuildId((config?.backendUi.minBuildId || '').trim())
        setState('update-required')
        return
      }

      if (config == null && readLock()) {
        setState('update-required')
        return
      }

      writeLock(false)
      setState('ready')
    }

    void run()

    const onFocus = () => {
      void run()
    }
    const interval = window.setInterval(() => {
      void run()
    }, 45_000)

    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', onFocus)
      window.clearInterval(interval)
    }
  }, [])

  const onUpdate = async () => {
    if (busy) return
    setBusy(true)
    setState('reloading')
    try {
      await clearCachesAndReload()
    } finally {
      setBusy(false)
    }
  }

  if (state === 'ready') return <>{children}</>

  if (state === 'checking') {
    return <BootSplash label="Starting console" testId="version-checking" />
  }

  if (state === 'reloading') {
    return <BootSplash label="Loading new version" testId="version-reloading" />
  }

  return (
    <div className="w7-version-gate" data-testid="version-update-required">
      <div className="w7-version-gate-card">
        <StrixMark className="w7-version-gate-mark w7-version-gate-mark--card" size={48} />
        <h1 className="w7-version-gate-title">Update required</h1>
        <p className="w7-version-gate-body">
          A newer version of the agent console is required before you can continue.
          No operations are available until you update.
        </p>
        {(requiredBuildId || currentBuildId) && (
          <p className="w7-dim w7-version-gate-meta">
            Current build <code>{currentBuildId}</code>
            {requiredBuildId ? (
              <>
                {' '}
                · required <code>{requiredBuildId}</code>
              </>
            ) : null}
          </p>
        )}
        <Button variant="primary" busy={busy} onClick={() => void onUpdate()}>
          Update now
        </Button>
      </div>
    </div>
  )
}
